/**
 * Signup blocklist — shared between client and server.
 *
 * Three categories of blocked signups:
 *   1) Public/personal email providers (gmail, yahoo, etc.) — must use a
 *      business email tied to the business website domain.
 *   2) Big companies likely to clone the platform — large tech, SaaS, and
 *      platform owners with engineering capacity and motivation.
 *   3) Direct competitors — existing social media management tools.
 *
 * Each category has its own list, message, and check function.
 * Domain matching: exact host OR any subdomain (mail.foo.com matches foo.com).
 * Substring (ROOT_NAMES) only used for competitors where brand-name typo-tolerance
 * is desirable.
 *
 * Consumers: both client SignupModal and server /api/auth/signup route.
 * Keep this file CommonJS so `require()` works from the Express backend;
 * webpack/CRA will auto-interop for the React import.
 */

const ROOT_NAMES = [
  'buffer',
  'hootsuite',
  'socialbee',
  'sproutsocial',
  'canva',
  'planoly',
  'loomly',
  'agorapulse',
  'meetedgar',
  'metricool',
  'publer',
  'postplanner',
  'sendible',
  'coschedule',
  'sprinklr',
  'socialpilot',
  'contentstudio',
  'brandwatch',
];

const EXACT_DOMAINS = [
  // Buffer
  'buffer.com',
  'buff.ly',
  // Hootsuite
  'hootsuite.com',
  'hsuite.co',
  'ow.ly',
  // Later
  'later.com',
  'latermedia.com',
  // SocialBee
  'socialbee.io',
  'socialbee.com',
  // Sprout Social
  'sproutsocial.com',
  'sprout.social',
  // Canva
  'canva.com',
  // Planoly
  'planoly.com',
  // Loomly
  'loomly.com',
  // Agorapulse
  'agorapulse.com',
  // MeetEdgar
  'meetedgar.com',
  // Metricool
  'metricool.com',
  // Publer
  'publer.io',
  'publer.com',
  // Postplanner
  'postplanner.com',
  // Sendible
  'sendible.com',
  // CoSchedule
  'coschedule.com',
  // Sprinklr
  'sprinklr.com',
  // SocialPilot
  'socialpilot.co',
  // ContentStudio
  'contentstudio.io',
  // Zoho (ZohoSocial is a product; exact match only — Zoho has many unrelated products)
  'zoho.com',
  // Brandwatch / Falcon
  'brandwatch.com',
  'falcon.io',
];

const BLOCK_MESSAGE = 'Sorry, no competitors allowed in the site';

// ── Public / personal email providers ────────────────────────────────────────
// Free webmail providers — owners of a real business should have a domain-
// matched business email. Mirrors the legacy client-only list.
const PUBLIC_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'mail.com',
  'email.com',
  'icloud.com',
  'me.com',
  'msn.com',
  'live.com',
  'protonmail.com',
  'zoho.com',
];

const PUBLIC_EMAIL_MESSAGE =
  'Please use your business email address. Public email providers are not allowed.';

// ── Role-based / shared mailbox LOCAL-PARTS ──────────────────────────────────
// The part BEFORE the @ in an email. These are catch-all mailboxes that aren't
// tied to a real person — info@, contact@, support@, etc. eHandshake associates
// content with a NAMED human (Jax says "Hi, I'm Jax", reviews are signed,
// posts show the author), so a signup from `contact@somecompany.com` produces
// an awkward "Hi, I'm Contact" profile.
//
// Match is exact on the normalized local part (lowercase, dots/hyphens/under-
// scores stripped — so "customer.service" / "customer-service" / "customer_service"
// / "customerservice" all collapse to the same blocked entry). Curated from the
// most common role-mailbox conventions across SMB sites + RFC 2142 reserved
// addresses + sales/marketing/HR catch-alls. Adding entries is cheap: append
// the normalized form (no separators) below.
const ROLE_BASED_LOCAL_PARTS = [
  // RFC 2142 reserved + universal admin mailboxes
  'admin', 'administrator', 'webmaster', 'postmaster', 'hostmaster',
  'abuse', 'noc', 'security', 'noreply', 'donotreply', 'nobody',
  // General contact / inquiry catch-alls
  'contact', 'contacts', 'contactus', 'getintouch', 'reachus', 'reachout',
  'info', 'information', 'inquiry', 'inquiries', 'enquiry', 'enquiries',
  'hello', 'hi', 'hey', 'office', 'mail', 'email', 'general',
  // Customer support / service
  'support', 'helpdesk', 'help', 'customerservice', 'customercare',
  'customersupport', 'service', 'services', 'care', 'feedback', 'complaints',
  // Sales / marketing / commercial
  'sales', 'marketing', 'biz', 'business', 'team', 'theteam', 'staff',
  'partnerships', 'partners', 'partner', 'affiliate', 'affiliates',
  'newsletter', 'newsletters', 'press', 'media', 'pr', 'publicity',
  // Operations / finance / legal
  'billing', 'accounts', 'accounting', 'finance', 'payments', 'invoice', 'invoices',
  'legal', 'compliance', 'privacy', 'dpo', 'gdpr',
  // HR / recruiting
  'hr', 'careers', 'jobs', 'recruiting', 'recruitment', 'talent', 'hiring',
  // Notifications / system
  'alerts', 'notifications', 'system', 'automated', 'auto',
  // Editorial / content
  'editor', 'editorial', 'content', 'submissions', 'submit',
  // Misc role-y patterns
  'orders', 'order', 'shipping', 'returns', 'reservations', 'booking', 'bookings',
  'events', 'membership', 'members', 'subscribers', 'subscription',
];

const ROLE_BASED_EMAIL_MESSAGE =
  'Please sign up with your personal work email (e.g., yourname@yourbusiness.com) — shared mailboxes like contact@, support@, info@, etc. can\'t be tied to a single profile.';

/**
 * Normalize an email local-part for blocklist matching.
 *   "Customer.Service@x.com" → "customerservice"
 *   "Support+test@x.com"     → "support"  (drop +tag)
 *   "help-desk@x.com"        → "helpdesk"
 *   "info_team@x.com"        → "infoteam" (then blocklist-matched against "info"/"team" — no, this doesn't match)
 * Tag-after-+ is dropped (Gmail-style filtering). Periods, hyphens, underscores
 * are stripped (treats "customer.service" / "customer-service" / "customer_service"
 * as the same blocked entry).
 */
function normalizeEmailLocalPart(email) {
  if (!email || typeof email !== 'string') return '';
  const at = email.indexOf('@');
  if (at < 1) return '';
  let local = email.slice(0, at).toLowerCase();
  // Drop +tag (Gmail / Fastmail / etc. subaddressing)
  const plus = local.indexOf('+');
  if (plus !== -1) local = local.slice(0, plus);
  // Strip separators commonly used in role mailboxes
  return local.replace(/[._\-]/g, '');
}

/**
 * Returns true if the email's local-part is a generic role / shared mailbox
 * that shouldn't be tied to a single profile (contact@, support@, info@, etc.).
 */
function isRoleBasedEmail(email) {
  const local = normalizeEmailLocalPart(email);
  if (!local) return false;
  return ROLE_BASED_LOCAL_PARTS.includes(local);
}

// ── Big companies likely to clone the platform ───────────────────────────────
// Large tech, SaaS, marketing, and platform companies that have the engineering
// capacity and adjacent products to replicate eHandshake quickly. Blocked at
// signup so we don't show them how the system works.
const BIG_COMPANY_DOMAINS = [
  // Big Tech (FAANG+)
  'google.com', 'alphabet.com', 'youtube.com', 'googlemail.com',
  'meta.com', 'facebook.com', 'fb.com', 'instagram.com', 'whatsapp.com', 'threads.net',
  'apple.com', 'mac.com',
  'amazon.com', 'aws.amazon.com', 'amazon.co.uk', 'amazon.de', 'twitch.tv',
  'microsoft.com', 'office.com', 'xbox.com', 'skype.com', 'github.com', 'linkedin.com',
  'netflix.com',
  // Major social / platform owners
  'twitter.com', 'x.com',
  'tiktok.com', 'bytedance.com', 'douyin.com',
  'pinterest.com',
  'snap.com', 'snapchat.com',
  'reddit.com',
  'discord.com',
  'tumblr.com', 'automattic.com', 'wordpress.com', 'wp.com', 'woocommerce.com',
  'bsky.app', 'bluesky.app',
  'mastodon.social',
  'yelp.com',
  'nextdoor.com',
  'medium.com', 'substack.com',
  'tencent.com', 'wechat.com', 'weixin.qq.com',
  'naver.com', 'kakaocorp.com', 'line.me', 'linecorp.com',
  // CRM / SaaS giants with adjacent marketing or SMB products
  'salesforce.com', 'slack.com', 'tableau.com',
  'adobe.com', 'marketo.com', 'magento.com',
  'oracle.com', 'eloqua.com', 'netsuite.com',
  'hubspot.com',
  'intuit.com', 'mailchimp.com', 'quickbooks.com',
  'zendesk.com',
  'freshworks.com',
  // E-commerce / SMB platforms
  'shopify.com',
  'square.com', 'squareup.com', 'block.xyz',
  'stripe.com',
  'wix.com',
  'squarespace.com',
  'godaddy.com',
  // Email / marketing platforms big enough to clone
  'klaviyo.com',
  'activecampaign.com',
  'constantcontact.com',
  'brevo.com', 'sendinblue.com',
  'iterable.com',
  'customer.io',
  'mailerlite.com',
  'drip.com',
  'convertkit.com', 'kit.com',
  'sendgrid.com', 'twilio.com',
  'braze.com',
  // Productivity / collab giants that could pivot
  'atlassian.com', 'jira.com', 'trello.com', 'confluence.com', 'bitbucket.org',
  'notion.so', 'notion.com',
  'airtable.com',
  'asana.com',
  'monday.com',
  'clickup.com',
  'zoom.us', 'zoom.com',
  'dropbox.com',
  'box.com',
  'gitlab.com',
  // Big enterprise tech
  'ibm.com', 'redhat.com',
  'sap.com',
  'servicenow.com',
  'workday.com',
  'cisco.com', 'webex.com',
  'hp.com', 'hpe.com',
  'dell.com',
  'intel.com',
  'nvidia.com',
  'amd.com',
  'qualcomm.com',
  'vmware.com',
  // Search / browsers / general
  'cloudflare.com',
  'duckduckgo.com',
  'yandex.com', 'yandex.ru',
  'baidu.com',
  'mozilla.org',
  'brave.com',
  'opera.com',
  // Streaming / media giants
  'spotify.com',
  'disney.com', 'disneyplus.com',
  'paramount.com',
  'warnermedia.com', 'wbd.com',
];

const SMALL_BIZ_ONLY_MESSAGE =
  'Sorry, eHandshake is built for small, local businesses.';

// ── Shared-employee SaaS / consulting domains ────────────────────────────────
// Mid-size SaaS, IT-services, and consulting firms where many employees share
// the corporate domain. Domain-match verification by itself can't prove the
// signer is the OWNER — at apps365 (the prompt that added this list, 2026-05-21),
// any of ~500 employees with an @apps365.com address would pass our domain
// check. Different failure mode than gmail (which has no business-domain match
// at all): here the match passes but doesn't carry the meaning we want.
//
// Curation policy: this list grows opportunistically. Adding speculative
// "might-be-multi-employee" domains creates false-positives for legitimate
// 5-person consultancies. Better to add domains we've actually seen drop
// hollow / abandoned signups (visible via Phase 6 daily digest) than to
// pre-block hypothetically.
//
// Distinct from BIG_COMPANY_DOMAINS: those are FAANG / FAANG-adjacent + direct
// platform competitors we don't want to educate. This list is for noise/dross
// — companies where a random employee's signup is just a curiosity click, not
// a clone risk.
const SHARED_EMPLOYEE_DOMAINS = [
  // Microsoft 365 / SharePoint / business-software consultancies
  'apps365.com',

  // Global IT services giants — Big 4 / Big 4-adjacent and Indian IT majors.
  // Any employee dropping their corporate URL is a curiosity click; none of
  // these employees would be "the owner" of a small business in our sense.
  'deloitte.com',
  'kpmg.com',
  'pwc.com',
  'ey.com',
  'accenture.com',
  'infosys.com',
  'tcs.com', 'tcs.in',
  'wipro.com',
  'cognizant.com',
  'capgemini.com',
  'hcltech.com', 'hcl.com',
  'mindtree.com',
  'mphasis.com',
  'techmahindra.com',
  // Add more as the Phase 6 daily digest surfaces them.
];

const SHARED_EMPLOYEE_MESSAGE =
  "This looks like a multi-employee company domain. eHandshake is built for " +
  "small-business owners — if you own this business directly, please email " +
  "support@ehandshake.com and we'll help you sign up.";

/**
 * Normalize any URL or domain-bearing string to a bare lowercase host.
 *   "HTTPS://WWW.Buffer.COM/about?x=1" → "buffer.com"
 *   "user@mail.Buffer.com"             → "mail.buffer.com"
 */
function normalizeDomain(input) {
  if (!input) return '';
  let s = String(input).trim().toLowerCase();
  // If it looks like an email, take the domain side.
  const at = s.indexOf('@');
  if (at !== -1) s = s.slice(at + 1);
  // Strip protocol and www.
  s = s.replace(/^[a-z]+:\/\//, '').replace(/^www\./, '');
  // Cut at first path/query/fragment separator.
  s = s.split(/[/?#]/)[0];
  return s;
}

/**
 * Returns true if `host` matches any entry in `list` exactly or as a subdomain.
 * E.g. mail.buffer.com matches buffer.com.
 */
function hostMatchesList(host, list) {
  for (const d of list) {
    if (host === d || host.endsWith('.' + d)) return true;
  }
  return false;
}

/**
 * Returns true if the given domain/email/URL belongs to a blocked competitor.
 * Checks both EXACT_DOMAINS (and any subdomain of them) and ROOT_NAMES
 * (substring anywhere in the hostname). Any hit = blocked.
 */
function isCompetitorDomain(input) {
  const host = normalizeDomain(input);
  if (!host) return false;
  if (hostMatchesList(host, EXACT_DOMAINS)) return true;
  for (const name of ROOT_NAMES) {
    if (host.includes(name)) return true;
  }
  return false;
}

/**
 * Returns true if the given email/URL is a public/personal email provider.
 * Useful for the email side of signup; not usually meaningful for the website.
 */
function isPublicEmailDomain(input) {
  const host = normalizeDomain(input);
  if (!host) return false;
  return hostMatchesList(host, PUBLIC_EMAIL_DOMAINS);
}

/**
 * Returns true if the given domain/email/URL belongs to a big company
 * that we don't want signing up (large tech, SaaS, platform owners).
 */
function isBigCompanyDomain(input) {
  const host = normalizeDomain(input);
  if (!host) return false;
  return hostMatchesList(host, BIG_COMPANY_DOMAINS);
}

/**
 * Returns true if the given email/URL is a mid-size SaaS / consulting domain
 * where many employees share the corporate identity. Domain-match passes for
 * any of their employees, so we can't trust the signer is the actual business
 * owner. See SHARED_EMPLOYEE_DOMAINS for the curated list + curation policy.
 */
function isSharedEmployeeDomain(input) {
  const host = normalizeDomain(input);
  if (!host) return false;
  return hostMatchesList(host, SHARED_EMPLOYEE_DOMAINS);
}

module.exports = {
  ROOT_NAMES,
  EXACT_DOMAINS,
  PUBLIC_EMAIL_DOMAINS,
  BIG_COMPANY_DOMAINS,
  SHARED_EMPLOYEE_DOMAINS,
  ROLE_BASED_LOCAL_PARTS,
  BLOCK_MESSAGE,
  PUBLIC_EMAIL_MESSAGE,
  SMALL_BIZ_ONLY_MESSAGE,
  SHARED_EMPLOYEE_MESSAGE,
  ROLE_BASED_EMAIL_MESSAGE,
  normalizeDomain,
  normalizeEmailLocalPart,
  isCompetitorDomain,
  isPublicEmailDomain,
  isBigCompanyDomain,
  isSharedEmployeeDomain,
  isRoleBasedEmail,
};
